<template>
  <div style="margin-right:2px">
    <div
      ref="draggableContainer"
      class="row draggable-options-container"
      :style="minHeightCss"
    >
      <div ref="draggable">
        <draggable
          :list="options"
          draggable=".draggable-option"
          class="draggable-columns"
          animation="150"
          filter=".form-control"
          :prevent-on-filter="false"
        >
          <div
            v-for="(option, index) in options"
            :key="`option` + `${index}`"
            :class="[{ 'draggable-option': !readOnly }]"
          >
            <div
              :class="{ hover: hoveringOver === index }"
              @mouseover="hoveringOver = index"
              @mouseout="hoveringOver = null"
            >
              <div v-if="index === 0" class="row prl">
                <div class="col-xs-6 col-xs-offset-1 plrs">
                  <label>{{ lang.get('fields.form.label.label') }}</label>
                </div>
                <div class="col-xs-4 plrs">
                  <label>
                    <i class="af-icons af-icons-lock align-middle "></i>
                    {{ lang.get('fields.form.options.label') }}

                    <help-icon
                      align="middle"
                      :content="lang.get('fields.form.options.help')"
                    ></help-icon>
                  </label>
                </div>
              </div>
              <div class="row prl">
                <div
                  v-if="!readOnly"
                  class="col-xs-1 text-center plrs prn mtm"
                  style="cursor:all-scroll"
                >
                  <span
                    class="af-icons af-icons-up-down handle dark-icon"
                  ></span>
                </div>
                <div
                  v-else
                  class="col-xs-1 text-center plrs prn mtm"
                  disabled="disabled"
                >
                  <span
                    class="af-icons af-icons-up-down handle"
                    disabled="disabled"
                  ></span>
                </div>
                <div class="col-xs-6 plrs">
                  <multilingual-option
                    ref="multilingual"
                    :index="index"
                    :supported-languages="supportedLanguages"
                    :resource="option.translated"
                    property="optionText"
                    :focused-item="selectedLabelFocus"
                    :focused-lang="focusLang"
                    :disabled="readOnly"
                    @input="handleLabelInput"
                    @change="handleChange"
                    @changeFocus="changeFocus"
                    @error="handleLabelError"
                  />
                </div>
                <div :class="['col-xs-4', 'plrs', { error: option.error }]">
                  <input
                    type="text"
                    class="form-control"
                    :aria-label="`internal-value-${index}`"
                    :value="option.key"
                    :disabled="!isEnabled(option) || readOnly"
                    :aria-invalid="!!option.error"
                    required="required"
                    @focusin="focusOption(index, true)"
                    @focusout="focusOption(index, false)"
                    @input="e => updateKey(index, e.target.value)"
                  />
                  <div
                    v-if="option.error && selectedOptionFocus === index"
                    class="alert-error sticky inline key-error text-left "
                    style="margin-left:-5px !important"
                  >
                    {{ option.error }}
                  </div>
                </div>
                <div class="col-xs-1 text-center plrs pln mtm">
                  <a
                    @click.prevent="
                      option.new ? remove(index) : confirmDelete(index)
                    "
                  >
                    <span
                      v-if="!readOnly"
                      class="af-icons af-icons-close-circle remove not-draggable dark-icon"
                    ></span>
                  </a>
                </div>
              </div>
              <div class="row">
                <div
                  :class="['col-xs-12', 'plrm', { marginTop5: multiLanguage }]"
                >
                  <inserter
                    v-if="!readOnly"
                    :active="active"
                    @clicked="addOption(index)"
                  >
                  </inserter>
                </div>
              </div>
            </div>
          </div>
        </draggable>
      </div>
    </div>
    <input
      id="internal-value-options"
      :value="fieldValue"
      name="options"
      type="hidden"
    />
    <div v-if="labelValues" v-for="language in supportedLanguages" :key="labelValues[language]">
      <input
        :name="getOptionTextName(language)"
        type="hidden"
        :value="labelValues[language]"
      />
    </div>

    <confirmation-modal
      modal-id="modal-target-delete-option"
      :show-modal="confirmDeletion > -1"
      :confirmation="lang.get('fields.alerts.delete-option')"
      :confirm-button-label="lang.get('buttons.delete')"
      :cancel-button-label="lang.get('buttons.cancel')"
      :close-on-confirm="true"
      @confirmed="remove(confirmDeletion)"
      @closed="confirmDeletion = -1"
    />

    <loader v-if="!isWrapped" :id="loaderId" />
  </div>
</template>

<script>
import $ from 'jquery';
import Draggable from 'vuedraggable';
import Inserter from '../../../lib/components/Shared/Inserter';
import MultilingualOption from '../../../lib/components/Translations/MultilingualOption';
import ConfirmationModal from '../../../lib/components/Shared/ConfirmationModal';
import { convertNewlines } from '../../../lib/translations.js';
import HelpIcon from '../../../lib/components/Shared/HelpIcon';
import Loader from '../../../lib/components/Loader';

export default {
  inject: ['lang'],
  components: {
    Loader,
    HelpIcon,
    Draggable,
    Inserter,
    MultilingualOption,
    ConfirmationModal
  },
  props: {
    field: {
      type: Object,
      required: true
    },
    supportedLanguages: {
      type: Array,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    isWrapped: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      options: [],
      errors: {},
      labelErrors: {},
      confirmDeletion: -1,
      hoveringOver: null,
      insertPosition: null,
      initialInsertPosition: 'initial',
      selectedLabelFocus: -1,
      selectedOptionFocus: -1,
      minHeight: 60,
      focusLang: this.lang.fallback,
    };
  },
  computed: {
    labelValues() {
      let labelValues = {};
      this.supportedLanguages.forEach((lang) => {
        labelValues[lang] = this.options.map(o => o.translated[lang]).join('\r\n');
      })
      return labelValues;
    },
    fieldValue(){
      return this.options.map(option => option.key).join('\r\n')
    },
    active() {
      return this.position === this.insertPosition;
    },
    minHeightCss() {
      return this.minHeight <= 800 ? `min-height:${this.minHeight}px` : '';
    },
    multiLanguage() {
      return this.supportedLanguages.length > 1;
    },
    loaderId() {
      return !this.isWrapped ? 'draggable-options-loader' : 'loader';
    }
  },
  watch: {
    minHeight() {
      if (this.minHeight === 800) {
        this.$refs.draggableContainer.classList.add('scroll-box-shadow');
      } else {
        this.$refs.draggableContainer.classList.remove('scroll-box-shadow');
      }
    },
    errors() {
      this.emitErrors();
    },
    labelErrors() {
      this.emitErrors();
    },

    options(currentOptions) {
      if (currentOptions.length === 0) {
        this.insert(-1);
      }
    },
    fieldValue() {
      this.handleLabelValueChange();
    },
    labelValues() {
      this.handleLabelValueChange();
    }
  },
  beforeDestroy: function() {
    this.$emit('errors', false);
  },
  mounted() {
    if (!this.field.options) {
      this.options = [];
    } else {
      let fieldOptions = this.field.options;
      if (typeof fieldOptions === 'string') {
        fieldOptions = fieldOptions
          .split('\r\n')
          .map(option => ({ id: option }));
      }

      this.options = fieldOptions.map((option, index) => ({
        key: option.id,
        new: option.id === '',
        translated: this.supportedLanguages.reduce(
          (trans, lang) => ({
            ...trans,
            [lang]:
              (this.field.translated[lang] || {}).optionText !== undefined
                ? this.field.translated[lang].optionText.split('\r\n')[index]
                : '\r\n'
          }),
          {}
        )
      }));
      if (this.options.length === 1) {
        this.validateOption(0, this.options[0].key);
      }
    }
  },
  updated() {
    this.$nextTick(() => {
      const min =
        (this.$refs.draggable.clientHeight > 0
          ? this.$refs.draggable.clientHeight
          : this.minHeight) + 5;
      this.minHeight = min < 800 ? min : 800;
    });
  },
  methods: {
    addOption(currentIndex) {
      this.insert(currentIndex);
    },
    setInsertPosition(key) {
      this.insertPosition = key;
    },
    resetInsertPosition() {
      this.insertPosition = null;
    },
    toggleSelector(key) {
      if (this.insertPosition === key) {
        this.resetInsertPosition();
        return;
      }

      this.setInsertPosition(key);
    },
    getIndex(options, key) {
      return options.map(option => option.key).indexOf(key);
    },
    confirmDelete(index) {
      this.confirmDeletion = index;
    },
    remove(index) {
      if (index === undefined) {
        index = this.confirmDeletion;
      }
      if (index >= 0) {
        this.options.splice(index, 1);

        this.confirmDeletion = -1;
        if (this.errors[index]) {
          this.setError(index, null);
        }
        this.handleLabelValueChange()
      }
    },
    insert(index, key, value, language, shift = false) {
      const insertAtIndex = index + 1;
      let added = 0;
      let translations = {};
      let option = {};
      this.supportedLanguages.forEach(lang => {
        translations[lang] = language === lang && value ? value : '';
      });
      if (shift) {
        const deleteCount = this.optionIsEmpty(insertAtIndex) ? 1 : 0;
        this.options.splice(insertAtIndex, deleteCount, {
          key: '',
          translated: translations,
          new: true
        });
      }
      if (this.options[insertAtIndex] !== undefined && value !== undefined) {
        option = this.options[insertAtIndex];

        if (language !== undefined) {
          option.translated[language] = translations[language];
        } else {
          option.translated = translations;
        }

        if (
          language === this.lang.fallback &&
          this.canUpdateKey(insertAtIndex, value)
        ) {
          option.key = key;
        }

        this.options[insertAtIndex] = option;
      } else {
        option = {
          key: key || '',
          translated: translations,
          new: true
        };

        this.options.splice(insertAtIndex, 0, option);
        added++;
      }
      this.resetInsertPosition();

      this.validateOption(insertAtIndex, option.key);
      if (value) {
        this.handleLabelValueChange();
      }

      return added;
    },
    optionIsEmpty (index) {
      return this.options[index] === undefined || (this.options[index].key.length === 0 && 
          !this.supportedLanguages.some(sl => this.options[index].translated[sl].length > 0))
    },
    handleLabelInput(value, language, index) {
      if (Array.isArray(value)) {
        if (value.length > 100) {
          this.showLoader().then(() => {
            this.parseLabelInput(value, language, index);
          });
          this.hideLoader();
        } else {
          this.parseLabelInput(value, language, index);
        }
      }
      if (language === this.lang.fallback) {
        if (
          this.canUpdateKey(index, value) &&
          (Math.abs(
            this.generateKey(value).length - this.options[index].key.length
          ) === 1 ||
            this.options[index].key === '')
        ) {
          this.updateKey(index, this.generateKey(value));
        }
      }
      
      if (!Array.isArray(value)) {
        this.handleLabelValueChange();
      }
    },
    parseLabelInput(value, language, index) {
      let added = 0;
      let position = index + 1;
      const shift = this.optionIsEmpty(index);

      value.forEach((part, i) => {
        part = part.trim();
        position = index - 1 + i;
        if (part !== '') {
          const key = language === this.lang.fallback ? this.generateKey(part) : '';
          added += this.insert(position, key, part, language, shift);
        }
      });
      if (added === 0) {
        if (position <= index) {
          position += index - position + 1;
        }
        if (this.options[position] !== undefined) {
          this.changeFocus(position, language);
        } else {
          this.insert(index, undefined, undefined, language);
          this.changeFocus(index + 1, language);
        }
      } else {
        this.changeFocus(position, language);
      }
    },
    getOptionTextName(language) {
      return `translated[optionText][${language}]`;
    },
    updateKey(index, value) {
      this.options[index].key = value;
      this.validateOption(index, value);
    },
    canUpdateKey(index, value) {
      return (
          !Array.isArray(value) &&
        this.options[index].new &&
        (this.options[index].key === '' ||
          this.generateKey(value).indexOf(this.options[index].key) === 0 ||
          this.options[index].key.indexOf(this.generateKey(value)) === 0)
      );
    },
    handleChange(value, language, index) {
      value = value.trim();
      this.options[index].translated[language] = value;
    },
    validateOption(index, value) {
      if (value === '') {
        this.options[index].error = this.lang.get(
          'validation.required_generic'
        );
        this.setError(index, true);
        return;
      }
      const keyCount = this.options.filter(option => option.key === value)
        .length;

      if (keyCount > 1) {
        this.options[index].error = this.lang.get(
          'fields.form.options.validation.unique'
        );
        this.setError(index, true);
      } else {
        this.options[index].error = null;
        this.setError(index, null);
      }
    },
    generateKey(value) {
      return value.toString().replace(/["']/g, '');
    },
    
    handleLabelValueChange() {
      const options = this.options.map(option => ({
        id: option.key,
        text: option.translated[this.getLocale()] || ''
      }));

      let translations = JSON.parse(JSON.stringify(this.field.translated));

      this.supportedLanguages.forEach(lang => {
        if (translations[lang] === undefined) {
          translations[lang] = {};
        }
        translations[lang]['optionText'] = this.labelValues[lang];
      });

      if (this.shouldUpdate(this.field.translated, translations) || this.shouldUpdate(
          options.map(option => option.id),
          this.field.options)) {
          this.$emit('input', {
            options,
            translated: convertNewlines(translations)
          });
          this.$emit('updated');
      }
    },
    shouldUpdate(compare1, compare2) {
      return JSON.stringify(compare1) !== JSON.stringify(compare2);
    },
    setError(index, value) {
      this.errors = {
        ...this.errors,
        [index]: value
      };
    },
    isEnabled(option) {
      return option.new;
    },
    changeFocus(index, lang) {
      this.selectedLabelFocus = index;
      this.focusLang = lang;
    },
    handleLabelError(index, value) {
      this.labelErrors = {
        ...this.labelErrors,
        [index]: value
      };
    },
    emitErrors() {
      this.$emit(
        'errors',
        Object.values(this.errors).some(error => error) ||
          Object.values(this.labelErrors).some(error => error)
      );
    },
    focusOption(index, value) {
      this.selectedLabelFocus = -1;
      this.selectedOptionFocus = value ? index : -1;
    },
    getLocale() {
      return this.supportedLanguages.some(
        language => language === this.lang.locale
      )
        ? this.lang.locale
        : this.lang.fallback;
    },
    showLoader() {
      return new Promise(resolve => {
        $('#' + this.loaderId)
          .show()
          .fadeTo(250, 0.8, () => resolve(true));
      });
    },

    hideLoader() {
      $('#' + this.loaderId).fadeTo(250, 0, function() {
        $(this).hide();
      });
    }
  }
};
</script>
<style scoped>
.key-error {
  margin: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.draggable-options-container {
  max-height: 700px;
  overflow-y: scroll;
  overflow-x: hidden;
}

.dark-icon {
  color: #000;
  font-size: 18px;
}

.row {
  padding: 0 5px;
}
.row.prl {
  padding-right: 15px;
}
.marginTop5 {
  margin-top: -5px;
}

.justified-label {
  display: block;
  text-align: justify;
  max-height: 20px !important;
  overflow: hidden;
  line-height: 20px;
}

.justified-label:after {
  content: '';
  display: inline-block;
  width: 100%;
}

.scroll-box-shadow {
  padding-top: 8px;
  box-shadow: inset 0px 11px 8px -9px #ccc, inset 0px -11px 8px -9px #ccc;
}
</style>
